import { useState, type ReactNode } from "react";
import type { JSONSchema } from "../sync/types";
import { t } from "../i18n";
import { Field, Form } from "./ui";

// SchemaForm builds a form from an object schema's properties: choices for
// enums, checkboxes, number and text fields. Anything else is edited as JSON.
// extra goes beside the submit button, as another way out of the form.
export function SchemaForm({ schema, submit, onSubmit, extra }: { schema: JSONSchema | null; submit: string; onSubmit: (v: unknown) => Promise<void>; extra?: ReactNode }) {
  const props = schema?.properties;
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [raw, setRaw] = useState("{}");
  const [error, setError] = useState("");

  async function send() {
    let v: unknown = values;
    if (!props) {
      try {
        v = JSON.parse(raw);
      } catch {
        setError(t("Enter valid JSON."));
        return;
      }
    }
    setError("");
    try {
      await onSubmit(v);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const set = (k: string, v: unknown) => setValues((old) => ({ ...old, [k]: v }));
  const field = (k: string, p: JSONSchema) => {
    const label = p.title ?? k;
    const required = schema?.required?.includes(k);
    const type = Array.isArray(p.type) ? p.type[0] : p.type;
    if (p.enum) {
      return (
        <Field key={k} label={label} hint={p.description}>
          <select
            required={required}
            value={k in values ? String(p.enum.indexOf(values[k])) : ""}
            onChange={(e) => set(k, p.enum![Number(e.target.value)])}
          >
            <option value="" disabled>
              {t("Choose…")}
            </option>
            {p.enum.map((o, i) => (
              <option key={i} value={i}>
                {String(o)}
              </option>
            ))}
          </select>
        </Field>
      );
    }
    if (type === "boolean") {
      return (
        <label key={k} className="check">
          <input type="checkbox" checked={values[k] === true} onChange={(e) => set(k, e.target.checked)} />
          {label}
        </label>
      );
    }
    if (type === "integer" || type === "number") {
      return (
        <Field key={k} label={label} hint={p.description}>
          <input type="number" required={required} min={p.minimum} max={p.maximum} step={type === "integer" ? 1 : "any"} onChange={(e) => set(k, e.target.value === "" ? undefined : Number(e.target.value))} />
        </Field>
      );
    }
    if (type === "string") {
      // An address is one line, however long it may be.
      const long = p.format !== "email" && p.format !== "uri" && (p.maxLength ?? 1000) > 200;
      return (
        <Field key={k} label={label} hint={p.description}>
          {long ? (
            <textarea required={required} minLength={p.minLength} maxLength={p.maxLength} onChange={(e) => set(k, e.target.value)} />
          ) : (
            <input type={p.format === "email" ? "email" : p.format === "uri" ? "url" : "text"} required={required} minLength={p.minLength} maxLength={p.maxLength} onChange={(e) => set(k, e.target.value)} />
          )}
        </Field>
      );
    }
    return (
      <Field key={k} label={t("{label} (JSON)", { label })} hint={p.description}>
        <textarea
          required={required}
          onChange={(e) => {
            try {
              set(k, JSON.parse(e.target.value));
            } catch {
              set(k, e.target.value);
            }
          }}
        />
      </Field>
    );
  };

  return (
    <Form className="schema-form" onSubmit={send}>
      {props ? Object.entries(props).map(([k, p]) => field(k, p)) : <textarea aria-label="JSON" value={raw} onChange={(e) => setRaw(e.target.value)} />}
      {error && <p className="error">{error}</p>}
      <div className="schema-actions">
        <button type="submit">{submit}</button>
        {extra}
      </div>
    </Form>
  );
}
